import { put, call, select, takeLatest } from 'redux-saga/effects';

import { errMessage } from '../../lib/apiError';
import { setUser, verifyOTPSuccess } from '../slices/auth';
import { selectMFAToken, selectEnrollmentToken } from '../selectors/mfa';
import {
  getMe,
  verifyMFA,
  enrollMFAStart,
  enrollMFAConfirm,
  verifyMFARecovery,
} from '../apis/auth';
import {
  clearMFA,
  verifyRequest,
  verifyFailure,
  recoveryRequest,
  recoveryFailure,
  clearScopedTokens,
  enrollStartRequest,
  enrollStartSuccess,
  enrollStartFailure,
  enrollConfirmRequest,
  enrollConfirmSuccess,
  enrollConfirmFailure,
} from '../slices/mfa';

function* completeLogin(response, { keepRecoveryCodes = false } = {}) {
  yield put(
    verifyOTPSuccess({
      access_token: response.access_token,
      refresh_token: response.refresh_token,
      expires_at: response.expires_at,
      user_id: response.user_id,
    })
  );
  // MFA responses omit user_data - fetch /me so the dashboard renders without
  // waiting for a navigation to lazy-trigger it.
  try {
    const me = yield call(getMe);
    if (me?.user_id) {
      const userData = { ...me };
      delete userData.success;
      yield put(setUser(userData));
    }
  } catch {
    // Non-fatal; dashboard will degrade and re-fetch on mount.
  }
  // Burn any leftover scoped tokens so they can't be replayed from memory.
  // keepRecoveryCodes keeps the post-enrollment modal data intact.
  yield put(keepRecoveryCodes ? clearScopedTokens() : clearMFA());
}

function* handleEnrollStart() {
  const token = yield select(selectEnrollmentToken);
  if (!token) {
    yield put(enrollStartFailure('Missing enrollment token. Sign in again.'));
    return;
  }
  try {
    const response = yield call(enrollMFAStart, token);
    if (response.success) {
      yield put(
        enrollStartSuccess({
          secret: response.secret,
          otpauthUri: response.otpauth_uri,
        })
      );
    } else {
      yield put(enrollStartFailure(response.error || 'Failed to start enrollment'));
    }
  } catch (error) {
    yield put(enrollStartFailure(errMessage(error, 'Failed to start enrollment')));
  }
}

function* handleEnrollConfirm(action) {
  const token = yield select(selectEnrollmentToken);
  if (!token) {
    yield put(enrollConfirmFailure('Missing enrollment token. Sign in again.'));
    return;
  }
  try {
    const response = yield call(enrollMFAConfirm, token, action.payload.code);
    if (response.success) {
      yield put(enrollConfirmSuccess(response.recovery_codes || []));
      yield* completeLogin(response, { keepRecoveryCodes: true });
    } else {
      yield put(enrollConfirmFailure(response.error || 'Invalid code'));
    }
  } catch (error) {
    yield put(enrollConfirmFailure(errMessage(error, 'Failed to confirm enrollment')));
  }
}

function* handleVerify(action) {
  const token = yield select(selectMFAToken);
  if (!token) {
    yield put(verifyFailure('Missing MFA token. Sign in again.'));
    return;
  }
  try {
    const response = yield call(
      verifyMFA,
      token,
      action.payload.code,
      Boolean(action.payload.trustDevice)
    );
    if (response.success) {
      yield* completeLogin(response);
    } else {
      yield put(verifyFailure(response.error || 'Invalid code'));
    }
  } catch (error) {
    yield put(verifyFailure(errMessage(error, 'Failed to verify code')));
  }
}

function* handleRecovery(action) {
  const token = yield select(selectMFAToken);
  if (!token) {
    yield put(recoveryFailure('Missing MFA token. Sign in again.'));
    return;
  }
  try {
    const response = yield call(verifyMFARecovery, token, action.payload.recoveryCode);
    if (response.success) {
      yield* completeLogin(response);
    } else {
      yield put(recoveryFailure(response.error || 'Invalid recovery code'));
    }
  } catch (error) {
    yield put(recoveryFailure(errMessage(error, 'Failed to verify recovery code')));
  }
}

export default function* mfaSaga() {
  yield takeLatest(enrollStartRequest.type, handleEnrollStart);
  yield takeLatest(enrollConfirmRequest.type, handleEnrollConfirm);
  yield takeLatest(verifyRequest.type, handleVerify);
  yield takeLatest(recoveryRequest.type, handleRecovery);
}
